"use client";

import { Button, Modal } from "@mantine/core";
import { TrustpilotFeedbackModal } from "@repo/dashboard-core";
import {
  getTrustpilotInvitationLink,
  submitFeedback,
} from "@repo/dashboard-core/actions";
import { useTranslations } from "next-intl";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  fetchTrustpilotSent,
  markTrustpilotSent,
} from "@/lib/actions/trustpilot";
import { useBrand } from "@/lib/context/BrandContext";
import { usePurchases } from "@/lib/context/PurchasesContext";

/** The services that ask for a review after a successful use (GD-120). */
export type ReviewedService =
  | "esim"
  | "calls"
  | "sms"
  | "phone-checker"
  | "earn-back";

type ServiceReview = {
  askForReview: (service: ReviewedService) => void;
  /** Sent to Trustpilot already, per member (GAPI-1081). */
  trustpilotSent: () => boolean;
  markSentToTrustpilot: () => void;
};

const ServiceReviewContext = createContext<ServiceReview>({
  askForReview: () => {},
  trustpilotSent: () => false,
  markSentToTrustpilot: () => {},
});

/**
 * GD-120 — after each successful use of a service, that service's success
 * message. Until the member has been sent to Trustpilot it carries the stars:
 * 4-5 go on to Trustpilot, and closing it without rating asks again next time.
 * From then on, on any device, it is the message alone.
 *
 * In the shell rather than on each page, so it outlives the page change SMS
 * makes once the message is sent.
 */
export function ServiceReviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations("dashboard");
  const brand = useBrand();
  const { topUps, esims } = usePurchases();
  const [service, setService] = useState<ReviewedService>();
  const [withStars, setWithStars] = useState(false);
  const [opened, setOpened] = useState(false);
  // A ref, not state: it is read when asking, and a call's ask can come from
  // a timer set long before (CallsView). Until the API answers, and if it
  // can't, the member is asked: once more costs less than never.
  const sentRef = useRef(false);

  useEffect(() => {
    void fetchTrustpilotSent().then((sent) => {
      sentRef.current ||= sent;
    });
  }, []);

  // The API only needs one of the customer's applications to know who they
  // are; the brand domain is what picks the Trustpilot business. The first
  // one that has an id: an eSIM bought in the Member Zone carries none.
  const applicationId = [...topUps, ...esims].find(
    (purchase) => purchase.applicationId,
  )?.applicationId;

  const trustpilotSent = useCallback(() => sentRef.current, []);

  const markSentToTrustpilot = useCallback(() => {
    sentRef.current = true;
    void markTrustpilotSent();
  }, []);

  // Decided when asking and kept while open: being sent to Trustpilot from
  // this very pop-up must not swap it for the message-only one.
  const askForReview = useCallback(
    (next: ReviewedService) => {
      setService(next);
      setWithStars(!sentRef.current && Boolean(applicationId));
      setOpened(true);
    },
    [applicationId],
  );

  const value = useMemo(
    () => ({ askForReview, trustpilotSent, markSentToTrustpilot }),
    [askForReview, trustpilotSent, markSentToTrustpilot],
  );

  const title = service && t(`feedback.service.${service}`);
  const close = () => setOpened(false);

  return (
    <ServiceReviewContext.Provider value={value}>
      {children}
      {applicationId && (
        <TrustpilotFeedbackModal
          opened={opened && withStars}
          onClose={close}
          applicationId={applicationId}
          trustpilotWebsiteId={brand.trustpilotWebsiteId}
          domain={brand.domain}
          submitFeedback={submitFeedback}
          getTrustpilotInvitationLink={getTrustpilotInvitationLink}
          title={title}
          redirectFrom={4}
          dismissible
          onRedirected={markSentToTrustpilot}
        />
      )}
      {/* The same frame as the stars' one, so the message reads the same. */}
      <Modal
        centered
        zIndex={1000}
        classNames={{
          overlay: "backdrop-blur-sm bg-black/40",
          header: "items-start",
          body: "flex flex-col py-0",
          content: "px-5 py-10 md:p-10",
        }}
        opened={opened && !withStars}
        onClose={close}
        closeButtonProps={{ "aria-label": t("feedback.close") }}
        size="md"
        title={
          <span className="text-3xl font-semibold text-primary">{title}</span>
        }
      >
        <Button className="ml-auto mt-4" onClick={close}>
          {t("feedback.close")}
        </Button>
      </Modal>
    </ServiceReviewContext.Provider>
  );
}

export const useServiceReview = () =>
  useContext(ServiceReviewContext).askForReview;

export function useTrustpilotSent() {
  const { trustpilotSent, markSentToTrustpilot } =
    useContext(ServiceReviewContext);
  return { trustpilotSent, markSentToTrustpilot };
}
